import { useEffect, useRef, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import API from "../../../services/api";
import {
  PhoneOutlined,
  EnvironmentOutlined,
  EyeOutlined,
  UserOutlined,
  ShopOutlined,
  GlobalOutlined,
  DollarOutlined,
  BankOutlined,
} from "@ant-design/icons";
import { FaWhatsapp } from "react-icons/fa";
import { Spin, Empty, message } from "antd";
import "./FranchiseDetails.css";

const TABS = ["Details", "Description", "Headquarters", "Branches"];

function FranchiseDetails() {
  const { slug } = useParams();
  const [franchise, setFranchise] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState(0);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [activeTab, setActiveTab] = useState("Details");
  const [imageErrors, setImageErrors] = useState({});
  const hasTrackedView = useRef(false);
  const stripRef = useRef(null);
  const [images, setImages] = useState([]);
  const [otherImages, setOtherImages] = useState([]);

  const handleNext = useCallback(() => {
    if (otherImages.length > 0) {
      setPreviewIndex((prev) => (prev + 1) % otherImages.length);
    }
  }, [otherImages.length]);

  const handlePrev = useCallback(() => {
    if (otherImages.length > 0) {
      setPreviewIndex((prev) => (prev === 0 ? otherImages.length - 1 : prev - 1));
    }
  }, [otherImages.length]);

  const handleTabChange = useCallback((tab) => {
    setActiveTab(tab);
  }, []);

  const handleImageError = useCallback((index, isMain = false) => {
    setImageErrors((prev) => ({ ...prev, [index]: true }));
    if (isMain && images.length > 1) {
      const nextAvailableIndex = images.findIndex((_, idx) => !imageErrors[idx] && idx !== index);
      if (nextAvailableIndex !== -1) {
        setActiveImage(nextAvailableIndex);
      }
    }
  }, [images, imageErrors]);

  const getVisitorId = () => {
    let visitorId = localStorage.getItem("visitorId");
    if (!visitorId) {
      visitorId = `guest_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem("visitorId", visitorId);
    }
    return visitorId;
  };

  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const isAdmin = user?.role === "admin";

  const formatDate = (date) => {
    if (!date) return "N/A";
    const parsed = new Date(date);
    return Number.isNaN(parsed.getTime()) ? "N/A" : parsed.toLocaleDateString();
  };

  const normalizePhone = (value) => value?.replace(/\D/g, "") || "";

  const renderDetailRow = (label, value) =>
    value !== null && value !== undefined && value !== "" ? (
      <tr key={label}>
        <td className="frn-detail-label">{label}</td>
        <td className="frn-detail-value">{value}</td>
      </tr>
    ) : null;

  const renderImageWithFallback = (src, alt, className, index, isMain = false) => {
    if (imageErrors[index]) {
      return (
        <div className={`${className} frn-image-fallback`}>
          <span>Image not available</span>
        </div>
      );
    }
    return (
      <img
        src={src}
        alt={alt}
        className={className}
        onError={() => handleImageError(index, isMain)}
        loading="lazy"
      />
    );
  };

  useEffect(() => {
    const fetchFranchise = async () => {
      try {
        setLoading(true);
        let res;
        try {
          res = await API.get(`/franchises/slug/${slug}`);
        } catch (err) {
          try {
            res = await API.get(`/franchises/${slug}`);
          } catch (error) {
            console.log("Both slug and ID fetch failed:", error);
            setFranchise(null);
            message.error("Failed to load franchise details");
            return;
          }
        }
        setFranchise(res.data);

        if (res.data?._id && !hasTrackedView.current) {
          hasTrackedView.current = true;
          try {
            const visitorId = getVisitorId();
            const viewRes = await API.post(
              `/franchises/${res.data._id}/view`,
              {},
              { headers: { "x-visitor-id": visitorId } }
            );
            setFranchise((prev) =>
              prev
                ? {
                  ...prev,
                  views:
                    typeof viewRes.data.views === "number"
                      ? viewRes.data.views
                      : prev.views,
                }
                : prev
            );
          } catch (viewError) {
            console.log("View count error:", viewError);
          }
        }
      } catch (err) {
        console.log(err);
        setFranchise(null);
        message.error("Error loading franchise details");
      } finally {
        setLoading(false);
      }
    };

    hasTrackedView.current = false;
    setActiveImage(0);
    fetchFranchise();
  }, [slug]);

  useEffect(() => {
    if (franchise) {
      const thumbnailOnly = franchise.thumbnail ? [franchise.thumbnail] : [];
      const otherImagesList = (franchise.images || []).filter((img) => img !== franchise.thumbnail);
      if (otherImagesList.length > 0) {
        setImages([otherImagesList[0]]);
        setOtherImages(otherImagesList);
      } else {
        setImages(thumbnailOnly);
        setOtherImages([]);
      }
      setImageErrors({});
      setActiveImage(0);
    }
  }, [franchise]);

  useEffect(() => {
    if (previewOpen && stripRef.current) {
      const activeThumb = stripRef.current.children[previewIndex];
      if (activeThumb) {
        activeThumb.scrollIntoView({ block: "nearest", inline: "center" });
      }
    }
  }, [previewIndex, previewOpen]);

  useEffect(() => {
    if (previewOpen) {
      document.body.classList.add("no-scroll");
    } else {
      document.body.classList.remove("no-scroll");
    }
    return () => {
      document.body.classList.remove("no-scroll");
    };
  }, [previewOpen]);

  if (loading) {
    return (
      <div className="frn-state">
        <Spin size="large" />
      </div>
    );
  }

  if (!franchise) {
    return (
      <div className="frn-state">
        <Empty description="Franchise not found" />
      </div>
    );
  }

  const phone = normalizePhone(franchise.contactDetails?.phone);
  const fullLocation = [
    franchise.headquarters?.address,
    franchise.headquarters?.city,
    franchise.headquarters?.state,
    franchise.headquarters?.country,
  ]
    .filter(Boolean)
    .join(", ");

  const cacheKey = franchise.updatedAt ? `?v=${encodeURIComponent(franchise.updatedAt)}` : "";

  return (
    <div className="frn-root">
      {/* Gallery */}
      <section className="frn-gallery">
        <div
          className="frn-gallery__main"
          onClick={() => {
            setPreviewIndex(0);
            setPreviewOpen(true);
          }}
        >
          {otherImages.length > 0 ? (
            renderImageWithFallback(
              otherImages[activeImage],
              franchise.franchiseDetails?.brandName || "Franchise",
              "frn-gallery__main-img",
              activeImage,
              true
            )
          ) : (
            images[0] ? (
              renderImageWithFallback(
                images[0],
                franchise.franchiseDetails?.brandName || "Franchise",
                "frn-gallery__main-img",
                0,
                true
              )
            ) : (
              <div className="frn-image-fallback">
                <span>Image not available</span>
              </div>
            )
          )}
          <div className="frn-gallery__overlay" />
          <button
            className="frn-gallery__badge"
            onClick={(e) => {
              e.stopPropagation();
              setPreviewIndex(0);
              setPreviewOpen(true);
            }}
            aria-label="View gallery"
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
            >
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <polyline points="21 15 16 10 5 21" />
            </svg>
            {otherImages.length > 0 ? otherImages.length : 0} photo{otherImages.length !== 1 ? "s" : ""}
          </button>
        </div>

        {otherImages.length > 0 && (
          <div className="frn-gallery__strip">
            {otherImages.map((img, i) => (
              <button
                key={i}
                className={`frn-gallery__thumb${activeImage === i ? " frn-gallery__thumb--on" : ""
                  }`}
                onClick={() => setActiveImage(i)}
                aria-label={`View photo ${i + 1}`}
              >
                {renderImageWithFallback(
                  img,
                  `Thumbnail ${i + 1}`,
                  "frn-gallery__thumb-img",
                  i
                )}
                {i === 4 && otherImages.length > 5 && (
                  <span className="frn-gallery__more">
                    +{otherImages.length - 5}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}
      </section>

      {/* Body */}
      <div className="frn-body">
        {/* Left Content */}
        <main className="frn-content">
          {/* Breadcrumb */}
          <nav className="frn-breadcrumb" aria-label="Breadcrumb">
            <span>Franchises</span>
            <span className="frn-bc-sep">/</span>
            <span>{franchise.headquarters?.city || "Listings"}</span>
            <span className="frn-bc-sep">/</span>
            <span className="frn-bc-active">{franchise.franchiseDetails?.brandName}</span>
          </nav>

          {/* Header */}
          <div className="frn-header">
            <div className="frn-chips">
              <span className="frn-chip frn-chip--type">
                {franchise.franchiseDetails?.category || "Franchise"}
              </span>
              <span
                className={`frn-chip frn-chip--status-${franchise.status === "active" ? "active" : "inactive"
                  }`}
              >
                {franchise.status}
              </span>
              {isAdmin && (
                <span
                  className={`frn-chip frn-chip--approval-${franchise.approvalStatus}`}
                >
                  {franchise.approvalStatus}
                </span>
              )}
              {franchise.isFeatured && (
                <span className="frn-chip frn-chip--featured">⭐ Featured</span>
              )}
            </div>

            <h1 className="frn-title">{franchise.franchiseDetails?.brandName}</h1>

            {fullLocation && (
              <p className="frn-location">
                <EnvironmentOutlined />
                {fullLocation}
              </p>
            )}
          </div>

          {/* Stats Bar */}
          <div className="frn-stats">
            <div className="frn-stat">
              <span className="frn-stat__icon">
                <DollarOutlined />
              </span>
              <span className="frn-stat__val">
                ₹ {franchise.franchiseDetails?.investmentRequired?.toLocaleString("en-IN") || 0}
              </span>
              <span className="frn-stat__lbl">Investment</span>
            </div>
            <div className="frn-stat__div" />

            <div className="frn-stat">
              <span className="frn-stat__icon">
                <BankOutlined />
              </span>
              <span className="frn-stat__val">
                ₹ {franchise.franchiseDetails?.franchiseFee?.toLocaleString("en-IN") || 0}
              </span>
              <span className="frn-stat__lbl">Franchise Fee</span>
            </div>
            <div className="frn-stat__div" />

            <div className="frn-stat">
              <span className="frn-stat__icon">
                <ShopOutlined />
              </span>
              <span className="frn-stat__val">{franchise.franchiseDetails?.outletCount || "—"}</span>
              <span className="frn-stat__lbl">Outlets</span>
            </div>
            <div className="frn-stat__div" />

            <div className="frn-stat">
              <span className="frn-stat__icon">
                <GlobalOutlined />
              </span>
              <span className="frn-stat__val">{franchise.franchiseDetails?.royaltyFee || 0}%</span>
              <span className="frn-stat__lbl">Royalty</span>
            </div>

            {isAdmin && (
              <>
                <div className="frn-stat__div" />
                <div className="frn-stat">
                  <span className="frn-stat__icon">
                    <EyeOutlined />
                  </span>
                  <span className="frn-stat__val">{franchise.views || 0}</span>
                  <span className="frn-stat__lbl">Views</span>
                </div>
              </>
            )}
          </div>

          {/* Tabs */}
          <div className="frn-tabs">
            <div className="frn-tabs__nav" role="tablist">
              {TABS.map((tab) => (
                <button
                  key={tab}
                  role="tab"
                  aria-selected={activeTab === tab}
                  className={`frn-tabs__btn${activeTab === tab ? " frn-tabs__btn--on" : ""
                    }`}
                  onClick={() => handleTabChange(tab)}
                >
                  {tab}
                </button>
              ))}
            </div>

            <div className="frn-tabs__panel">
              {activeTab === "Details" && (
                <div className="frn-detail-grid">
                  <div className="frn-detail-card">
                    <h3 className="frn-detail-card__head">Franchise Details</h3>
                    <table className="frn-detail-table">
                      <tbody>
                        {isAdmin && renderDetailRow("Franchise ID", franchise._id)}
                        {renderDetailRow("Brand Name", franchise.franchiseDetails?.brandName)}
                        {renderDetailRow("Category", franchise.franchiseDetails?.category)}
                        {renderDetailRow("Investment Required", franchise.franchiseDetails?.investmentRequired ? `₹ ${franchise.franchiseDetails.investmentRequired.toLocaleString("en-IN")}` : null)}
                        {renderDetailRow("Franchise Fee", franchise.franchiseDetails?.franchiseFee ? `₹ ${franchise.franchiseDetails.franchiseFee.toLocaleString("en-IN")}` : null)}
                        {renderDetailRow("Royalty Fee", franchise.franchiseDetails?.royaltyFee ? `${franchise.franchiseDetails.royaltyFee}%` : null)}
                        {renderDetailRow("Outlet Count", franchise.franchiseDetails?.outletCount)}
                        {renderDetailRow("Area Required", franchise.franchiseDetails?.areaRequired ? `${franchise.franchiseDetails.areaRequired} sqft` : null)}
                        {renderDetailRow("Status", franchise.status)}
                        {isAdmin && renderDetailRow("Approval Status", franchise.approvalStatus)}
                        {isAdmin && renderDetailRow("Views", franchise.views || 0)}
                        {isAdmin && renderDetailRow("Slug", franchise.slug)}
                      </tbody>
                    </table>
                  </div>

                  <div className="frn-detail-card">
                    <h3 className="frn-detail-card__head">Additional Information</h3>
                    <table className="frn-detail-table">
                      <tbody>
                        {renderDetailRow("HQ City", franchise.headquarters?.city)}
                        {renderDetailRow("HQ State", franchise.headquarters?.state)}
                        {renderDetailRow("HQ Country", franchise.headquarters?.country)}
                        {renderDetailRow("Total Branches", franchise.branches?.length || 0)}
                        {renderDetailRow("Featured", franchise.isFeatured ? "Yes" : "No")}
                        {isAdmin && renderDetailRow("Featured Until", formatDate(franchise.featuredUntil))}
                        {isAdmin && renderDetailRow("Expiry Date", formatDate(franchise.expiryDate))}
                        {renderDetailRow("Posted On", formatDate(franchise.createdAt))}
                        {isAdmin && renderDetailRow("Updated On", formatDate(franchise.updatedAt))}
                        {isAdmin && renderDetailRow("Owner Name", franchise.ownerId?.name)}
                        {isAdmin && renderDetailRow("Owner Email", franchise.ownerId?.email)}
                        {isAdmin && renderDetailRow("Total Images", franchise.images?.length || 0)}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {activeTab === "Description" && (
                <div className="frn-desc-card">
                  <p className="frn-desc-text">
                    {franchise.description || "No description available."}
                  </p>
                </div>
              )}

              {activeTab === "Headquarters" && (
                <div className="frn-branches-card">
                  <h3 className="frn-detail-card__head">Headquarters Location</h3>

                  <div className="frn-branch-item">
                    <div className="frn-branch-header">
                      <EnvironmentOutlined className="frn-branch-icon" />
                      <span className="frn-branch-name">
                        {franchise.franchiseDetails?.brandName || "Headquarters"}
                      </span>
                    </div>

                    <div className="frn-branch-details">
                      {franchise.headquarters?.locality && (
                        <div className="frn-branch-detail">
                          <span className="frn-branch-label">Locality:</span>
                          <span className="frn-branch-value">
                            {franchise.headquarters.locality}
                          </span>
                        </div>
                      )}
                      {franchise.headquarters?.city && (
                        <div className="frn-branch-detail">
                          <span className="frn-branch-label">City:</span>
                          <span className="frn-branch-value">
                            {franchise.headquarters.city}
                          </span>
                        </div>
                      )}

                      {franchise.headquarters?.state && (
                        <div className="frn-branch-detail">
                          <span className="frn-branch-label">State:</span>
                          <span className="frn-branch-value">
                            {franchise.headquarters.state}
                          </span>
                        </div>
                      )}

                      {franchise.headquarters?.country && (
                        <div className="frn-branch-detail">
                          <span className="frn-branch-label">Country:</span>
                          <span className="frn-branch-value">
                            {franchise.headquarters.country}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* ✅ OPTIONAL MAP */}
                  {franchise.headquarters?.coordinates && (
                    <iframe
                      width="100%"
                      height="250"
                      style={{
                        border: 0,
                        borderRadius: "10px",
                        marginTop: "15px",
                      }}
                      loading="lazy"
                      src={`https://www.google.com/maps?q=${franchise.headquarters.coordinates.coordinates[1]},${franchise.headquarters.coordinates.coordinates[0]}&z=14&output=embed`}
                    />
                  )}
                </div>
              )}

              {activeTab === "Branches" && (
                <div className="frn-branches-card">
                  <h3 className="frn-detail-card__head">Branch Locations</h3>
                  <div className="frn-branches-list">
                    {franchise.branches?.length > 0 ? (
                      franchise.branches.map((branch, index) => (
                        <div key={index} className="frn-branch-item">
                          <div className="frn-branch-header">
                            <ShopOutlined className="frn-branch-icon" />
                            <span className="frn-branch-name">{branch.branchName || `Branch ${index + 1}`}</span>
                          </div>
                          <div className="frn-branch-details">
                            {branch.city && (
                              <div className="frn-branch-detail">
                                <span className="frn-branch-label">City:</span>
                                <span className="frn-branch-value">{branch.city}</span>
                              </div>
                            )}
                            {branch.state && (
                              <div className="frn-branch-detail">
                                <span className="frn-branch-label">State:</span>
                                <span className="frn-branch-value">{branch.state}</span>
                              </div>
                            )}
                            {branch.address && (
                              <div className="frn-branch-detail">
                                <span className="frn-branch-label">Address:</span>
                                <span className="frn-branch-value">{branch.address}</span>
                              </div>
                            )}
                            {branch.phone && (
                              <div className="frn-branch-detail">
                                <span className="frn-branch-label">Phone:</span>
                                <span className="frn-branch-value">{branch.phone}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="frn-empty-msg">No branches listed for this franchise.</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Rejection Notice */}
          {franchise.approvalStatus === "rejected" && franchise.rejectionReason && (
            <div className="frn-rejection">
              <strong>Rejection Reason</strong>
              <p>{franchise.rejectionReason}</p>
            </div>
          )}
        </main>

        {/* Sidebar */}
        <aside className="frn-sidebar">
          <div className="frn-sidebar__card">
            <div className="frn-sidebar__price">
              <span className="frn-sidebar__amt">
                ₹ {franchise.franchiseDetails?.investmentRequired?.toLocaleString("en-IN") || 0}
              </span>
              <span className="frn-sidebar__per">Investment</span>
            </div>

            <hr className="frn-sidebar__rule" />

            <div className="frn-sidebar__owner">
              <span className="frn-sidebar__avatar" aria-hidden="true">
                <UserOutlined />
              </span>
              <div className="frn-sidebar__owner-info">
                <span className="frn-sidebar__owner-name">
                  {franchise.contactDetails?.contactName || franchise.ownerId?.name || "Contact Owner"}
                </span>
                <span className="frn-sidebar__owner-role">Franchise Owner</span>
              </div>
            </div>

            {(franchise.contactDetails?.phone || franchise.contactDetails?.email) && (
              <dl className="frn-sidebar__contact-list">
                {franchise.contactDetails?.phone && (
                  <>
                    <dt>Phone</dt>
                    <dd>{franchise.contactDetails.phone}</dd>
                  </>
                )}
                {franchise.contactDetails?.email && (
                  <>
                    <dt>Email</dt>
                    <dd className="frn-sidebar__email">{franchise.contactDetails.email}</dd>
                  </>
                )}
                {franchise.contactDetails?.website && (
                  <>
                    <dt>Website</dt>
                    <dd className="frn-sidebar__email">{franchise.contactDetails.website}</dd>
                  </>
                )}
              </dl>
            )}

            {phone && (
              <div className="frn-sidebar__actions">
                <a href={`tel:${phone}`} className="frn-btn frn-btn--call">
                  <PhoneOutlined style={{ transform: "rotate(90deg)" }} />
                  Call Now
                </a>
                <a
                  href={`https://wa.me/${phone}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="frn-btn frn-btn--wa"
                >
                  <FaWhatsapp />
                  WhatsApp
                </a>
              </div>
            )}

            <hr className="frn-sidebar__rule" />

            <dl className="frn-sidebar__facts">
              <div className="frn-sidebar__fact">
                <dt>Franchise Fee</dt>
                <dd>₹ {franchise.franchiseDetails?.franchiseFee?.toLocaleString("en-IN") || 0}</dd>
              </div>
              <div className="frn-sidebar__fact">
                <dt>Royalty Fee</dt>
                <dd>{franchise.franchiseDetails?.royaltyFee || 0}%</dd>
              </div>
              <div className="frn-sidebar__fact">
                <dt>Outlets</dt>
                <dd>{franchise.franchiseDetails?.outletCount || "—"}</dd>
              </div>
              <div className="frn-sidebar__fact">
                <dt>Category</dt>
                <dd>{franchise.franchiseDetails?.category || "—"}</dd>
              </div>
              <div className="frn-sidebar__fact">
                <dt>Posted</dt>
                <dd>
                  {franchise.createdAt
                    ? new Date(franchise.createdAt).toLocaleDateString()
                    : "—"}
                </dd>
              </div>
            </dl>

            {isAdmin && (
              <div className="frn-sidebar__admin">
                <span className="frn-sidebar__admin-label">
                  Listed by (Admin)
                </span>
                <span>{franchise.ownerId?.name || "N/A"}</span>
                <span>{franchise.ownerId?.email || "N/A"}</span>
              </div>
            )}
          </div>

          <p className="frn-sidebar__note">
            Listed on{" "}
            {franchise.createdAt
              ? new Date(franchise.createdAt).toLocaleDateString("en-IN", {
                day: "numeric",
                month: "long",
                year: "numeric",
              })
              : "—"}
          </p>
        </aside>
      </div>

      {/* Mobile Sticky Footer */}
      {phone && (
        <div
          className="frn-mobile-bar"
          role="complementary"
          aria-label="Contact actions"
        >
          <div className="frn-mobile-bar__price">
            <span className="frn-mobile-bar__amt">
              ₹ {franchise.franchiseDetails?.investmentRequired?.toLocaleString("en-IN") || 0}
            </span>
            <span className="frn-mobile-bar__per">Investment</span>
          </div>
          <div className="frn-mobile-bar__btns">
            <a href={`tel:${phone}`} className="frn-mbtn frn-mbtn--call">
              <PhoneOutlined style={{ transform: "rotate(90deg)" }} /> Call
            </a>
            <a
              href={`https://wa.me/${phone}`}
              target="_blank"
              rel="noopener noreferrer"
              className="frn-mbtn frn-mbtn--wa"
            >
              <FaWhatsapp /> WhatsApp
            </a>
          </div>
        </div>
      )}

      {/* Lightbox */}
      {previewOpen && (
        <div
          className="frn-lb"
          role="dialog"
          aria-modal="true"
          aria-label="Image preview"
          onClick={() => setPreviewOpen(false)}
        >
          <div
            className="frn-lb__blur"
            style={{ backgroundImage: `url(${otherImages[previewIndex] || images[0]})` }}
          />

          <button
            className="frn-lb__close"
            onClick={() => setPreviewOpen(false)}
            aria-label="Close preview"
          >
            ✕
          </button>

          <span className="frn-lb__counter">
            {previewIndex + 1} / {otherImages.length}
          </span>

          <div className="frn-lb__stage" onClick={(e) => e.stopPropagation()}>
            <button
              className="frn-lb__nav frn-lb__nav--prev"
              onClick={handlePrev}
              aria-label="Previous image"
            >
              ‹
            </button>
            {renderImageWithFallback(
              otherImages[previewIndex],
              `Preview ${previewIndex + 1}`,
              "frn-lb__img",
              previewIndex
            )}
            <button
              className="frn-lb__nav frn-lb__nav--next"
              onClick={handleNext}
              aria-label="Next image"
            >
              ›
            </button>
          </div>

          {otherImages.length > 1 && (
            <div
              className="frn-lb__strip"
              ref={stripRef}
              onClick={(e) => e.stopPropagation()}
            >
              {otherImages.map((img, i) => (
                <button
                  key={i}
                  className={`frn-lb__thumb${previewIndex === i ? " frn-lb__thumb--on" : ""
                    }`}
                  onClick={() => setPreviewIndex(i)}
                  aria-label={`Go to photo ${i + 1}`}
                >
                  {renderImageWithFallback(img, `Thumbnail ${i + 1}`, "", i)}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default FranchiseDetails;